import {Content} from "../../../_metronic/layout/components/content";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {RootState} from "../../core/store/store.ts";
import {useIntl} from "react-intl";
import searchTalentRequest from "../../core/api/requests/search/searchTalentRequest.ts";
import {useEffect, useState, useCallback} from "react";
import {addSelected, clearFilter, removeSelected} from "../../core/store/slices/searcher.ts";
import {setPackageValues} from "../../core/store/slices/package.ts";
import IAgencyTalent from "../../core/interfaces/IAgencyTalent.ts";
import TalentCard from "../../utils/TalentCard.tsx";
import { KTIcon, KTSVG } from "../../../_metronic/helpers/index.ts";
import ERoutes from "../../core/enums/ERoutes.ts";

export default function SearchPage() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searcherSlice = useSelector((state: RootState) => state.searcher)

  const [ talents, setTalents ] = useState<IAgencyTalent[]>([])

  const [ total, setTotal ] = useState<number>(1)

  const [ currentPage, setCurrentPage ] = useState<number>(1)

  const [ loading, setLoading ] = useState<boolean>(true)
  const [ loadingMore, setLoadingMore ] = useState<boolean>(false)

  const [ error, setError ] = useState<string | null>(null)

  const clear = () => {
    dispatch(clearFilter())
  }
  
  const searchTalent = async (search: string, page: number, append: boolean = false) => {
    if (page && page < 1) { setLoading(false); setError(null); return }
    if (page && page > total && !append) { setLoading(false); setError(null); return }
    if (append && loadingMore) return

    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      let filters = {...searcherSlice.filter}

      const f = {
        ...filters,
        query: search,
        page: page || currentPage,
        warningsDate: new Date().toISOString().split('T')[0],
      }

      const response = await searchTalentRequest(dispatch, f)

      if (response?.statusCode !== 200) {
        const msg = (response?.data as any)?.message || intl.formatMessage({ id: 'ERRORS.SOMETHING_WAS_WRONG_TRY_AGAIN_LATER' })
        setError(msg)
        if (!append) setTalents([])
        return
      }

      const data = response?.data?.data ?? []
      const pagination = response?.data?.pagination

      if (append) {
        setTalents(prev => [...prev, ...(Array.isArray(data) ? data : [])])
      } else {
        setTalents(Array.isArray(data) ? data : [])
      }
      setTotal(pagination?.total ?? 0)
      setCurrentPage(pagination?.page ?? 1)
    } catch (e: any) {
      setError(e?.message || intl.formatMessage({ id: 'ERRORS.SOMETHING_WAS_WRONG_TRY_AGAIN_LATER' }))
      if (!append) setTalents([])
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    clear()
  }, [searcherSlice.open]);

  const [ TO, setTO ] = useState<NodeJS.Timeout>()
  useEffect(() => {
    if (TO) {
      clearTimeout(TO)
    }

    const id = setTimeout(() => {
      searchTalent(searcherSlice.commonSearch ?? '', 1)
    }, 500)
    setTO(id)
    return () => clearTimeout(id)
  }, [searcherSlice.open, searcherSlice.commonSearch, searcherSlice.filter]);

  const handleScroll = useCallback(() => {
    if (loadingMore || loading) return
    if (currentPage >= total) return

    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollHeight = document.documentElement.scrollHeight
    const clientHeight = window.innerHeight

    if (scrollTop + clientHeight >= scrollHeight - 200) {
      searchTalent(searcherSlice.commonSearch ?? '', currentPage + 1, true)
    }
  }, [currentPage, total, loadingMore, loading, searcherSlice.commonSearch])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const handleCreatePackage = () => {
    const selected = searcherSlice.selected || []
    if (selected.length === 0) return

    const talentsData = selected.map((talent) => ({
      talent,
      status: 'pending',
      images: [] as string[],
      notes: '',
    }))

    dispatch(setPackageValues({
      key: ERoutes.PACKAGES_TALENTS,
      data: talentsData,
      loaded: true,
    }))

    navigate(ERoutes.PACKAGES_TALENTS.replace(':id', ERoutes.NEW_PARAMETER))
  }

  return <>
    <Content>
      <div className='row g-5 gx-xxl-8'>
        <div className='col-12'>
          <div className={'card'}>
            <div className='card-body d-flex flex-column'>
              {loading && (
                <div className='d-flex justify-content-center align-items-center py-15'>
                  <span className='spinner-border spinner-border-lg text-primary' role='status' />
                </div>
              )}

              {!loading && talents.length > 0 &&
                <>
                  <div className={'row row-cols-1 row-cols-xl-2'}>
                    {(talents || []).map((item) => {
                      const isSelected = (searcherSlice.selected || []).some((t) => t.id === item.talent.id)
                      return (
                        <TalentCard
                          key={item.talent.id}
                          talent={item.talent}
                          out={item.out}
                          allowSelect
                          selected={isSelected}
                          onSelect={() => {
                            if (isSelected) {
                              dispatch(removeSelected(item.talent))
                            } else {
                              dispatch(addSelected(item.talent))
                            }
                          }}
                        />
                      )
                    })}
                  </div>
                  {loadingMore && (
                    <div className="text-center py-5">
                      <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                      {intl.formatMessage({ id: 'PLACEHOLDERS.LOADING' })}
                    </div>
                  )}
                  {!loadingMore && currentPage < total && (
                    <div className="text-center py-3 text-muted">
                      {intl.formatMessage({ id: 'PLACEHOLDERS.SCROLL_FOR_MORE' })}
                    </div>
                  )}
                </>
              }

              {!loading && error &&
                <div className="alert alert-dismissible bg-light-danger d-flex flex-column flex-sm-row p-5 mb-10">
                  <span className="svg-icon svg-icon-2hx svg-icon-danger me-4 mb-5 mb-sm-0">
                    <KTSVG path="media/icons/duotune/general/gen044.svg" className="svg-icon-muted svg-icon-2hx" />
                  </span>
                  <div className="d-flex flex-column text-danger pe-0 pe-sm-10">
                    <h4 className="mb-1">{intl.formatMessage({ id: 'ERRORS.UPS' })}</h4>
                    <span>{error}</span>
                  </div>
                </div>
              }

              {!loading && !error && talents.length === 0 &&
                <div className="alert alert-dismissible bg-light-primary d-flex flex-column flex-sm-row p-5 mb-10">
                  <span className="svg-icon svg-icon-2hx svg-icon-primary me-4 mb-5 mb-sm-0">
                    <KTSVG path="media/icons/duotune/general/gen004.svg" className="svg-icon-muted svg-icon-2hx" />
                  </span>
                  <div className="d-flex flex-column text-primary pe-0 pe-sm-10">
                    <h4 className="mb-1">{intl.formatMessage({ id: 'ALERTS.NO_TALENTS_FOUND' })}</h4>
                    <span>{intl.formatMessage({ id: 'ALERTS.NO_RESULTS_FOUND_TRY_ANOTHER_SEARCH' })}</span>
                  </div>
                </div>
              }

            </div>
          </div>
        </div>

      </div>
    </Content>

      {searcherSlice.selected && searcherSlice.selected.length > 0 && (
        <div
          className="position-fixed bottom-0 end-0 m-5"
          style={{ zIndex: 1050 }}
        >
          <button
            type="button"
            className="btn btn-primary btn-lg shadow-lg d-flex align-items-center gap-2"
            onClick={handleCreatePackage}
          >
            <KTIcon iconName="plus" className="fs-2" />
            {intl.formatMessage({ id: 'PLACEHOLDERS.CREATE_PACKAGE' })}
          </button>
        </div>
      )}
    </>
}
